import { EditNote, Translate } from '@mui/icons-material';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Radio,
    RadioGroup,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import videoCRUDServiceInstance from '../../services/VideoCRUDService';
import videoUploadServiceInstance from '../../services/VideoUploadService';
import { getLanguagesForCreateVideo } from '../subtitles/SelectLanguageComponent';
import { useTrackUsage } from '../../hooks/useTrackUsage';

// TabPanel component for handling tab content
const TabPanel = (props) => {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`language-tabpanel-${index}`}
            aria-labelledby={`language-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
};

const VoiceOverDialog = ({ open, onClose, videoId, onSuccess, folderPath }) => {
    useTrackUsage('VoiceOverDialog', 'dialog', 'folder-browser', open);
    const [loading, setLoading] = useState(false);
    const [allLanguages, setAllLanguages] = useState([]);
    const [videoName, setVideoName] = useState('');
    const [englishAvailable, setEnglishAvailable] = useState(false);
    const [englishDownloadable, setEnglishDownloadable] = useState(false);
    const [currentTab, setCurrentTab] = useState(0);
    const [voMode, setVoMode] = useState('copy'); // 'ai' | 'copy'

    useEffect(() => {
        if (open && videoId) {
            setLoading(true);

            // Get all available languages
            setAllLanguages(getLanguagesForCreateVideo());

            // Fetch video details to get available languages
            videoCRUDServiceInstance
                .getVideoById(videoId)
                .then((response) => {
                    const videoData = response.data;

                    // Check if English is available and downloadable
                    let englishLang = null;
                    if (videoData.videoLanguages && Array.isArray(videoData.videoLanguages)) {
                        englishLang = videoData.videoLanguages.find(
                            (lang) => String(lang.languageName).toLowerCase() === 'english',
                        );
                    }

                    // English is available if it has status READY and VTT file
                    const isEnglishAvailable = englishLang && englishLang.status === 'READY' && englishLang.vttFileUrl;

                    // English is downloadable if it has voiceOverUrl
                    const isEnglishDownloadable = englishLang && englishLang.voiceOverUrl;

                    setEnglishAvailable(isEnglishAvailable);
                    setEnglishDownloadable(isEnglishDownloadable);
                    setVideoName(videoData.videoName);
                    setLoading(false);
                })
                .catch((error) => {
                    console.error('Error fetching video details:', error);
                    setLoading(false);
                    onClose();
                });
        } else {
            // Reset state when dialog closes
            setCurrentTab(0);
            setVoMode('copy');
        }
    }, [open, videoId]);

    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
    };

    const handleGenerateVoiceOver = () => {
        setLoading(true);

        // Get the current user's credentials
        const userData = JSON.parse(localStorage.getItem('authUser'));
        if (!userData) {
            setLoading(false);
            onClose('error', 'User authentication information not found');
            return;
        }

        const sourceLanguage = 'English';
        const targetLanguages = ['English'];
        const skipAI = voMode === 'copy';

        // Call API to start voiceover generation
        videoUploadServiceInstance
            .generateVoiceOver(videoId, videoName, sourceLanguage, targetLanguages.join(','), skipAI)
            .then((response) => {
                setLoading(false);
                onSuccess({
                    sourceLanguage: sourceLanguage,
                    targetLanguages: targetLanguages,
                });
                const modeLabel = skipAI
                    ? `English SRT has been copied to "English-VO" language.`
                    : `VO script generation from ${sourceLanguage} has been initiated. This will create "English-VO" language for editing and translation.`;
                onClose('success', modeLabel);
            })
            .catch((error) => {
                setLoading(false);
                console.error('Error starting VO script generation:', error);
                onClose(
                    'error',
                    `Failed to start VO script generation: ${error.response?.data?.message || error.message}`,
                );
            });
    };

    return (
        <Dialog open={open} onClose={() => !loading && onClose()} fullWidth maxWidth="md">
            <DialogTitle>VO Script Generation</DialogTitle>

            <DialogContent dividers>
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                )}

                {!loading && (
                    <>
                        {!englishAvailable ? (
                            <Typography color="error">
                                This video does not have English subtitles processed that can be used as a source for
                                voice over generation.
                            </Typography>
                        ) : (
                            <Box sx={{ width: '100%' }}>
                                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                    <Tabs value={0} variant="scrollable" scrollButtons="auto">
                                        <Tab label="English" id="language-tab-0" />
                                    </Tabs>
                                </Box>

                                <TabPanel value={0} index={0}>
                                    <Box sx={{ mb: 3 }}>
                                        <Paper
                                            elevation={1}
                                            sx={{
                                                p: 3,
                                                mb: 3,
                                                borderRadius: 2,
                                            }}
                                        >
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    mb: 2,
                                                    fontWeight: 'bold',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                }}
                                            >
                                                <EditNote />
                                                Generating this will add "English-VO" language
                                            </Typography>

                                            <RadioGroup
                                                value={voMode}
                                                onChange={(e) => setVoMode(e.target.value)}
                                                sx={{ mb: 2 }}
                                            >
                                                <FormControlLabel
                                                    value="ai"
                                                    control={<Radio />}
                                                    label={
                                                        <Box>
                                                            <Typography fontWeight={600}>AI-Generated VO Script</Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                Sends English subtitles to the AI server to produce a
                                                                voice-over adapted script.
                                                            </Typography>
                                                        </Box>
                                                    }
                                                />
                                                <FormControlLabel
                                                    value="copy"
                                                    control={<Radio />}
                                                    label={
                                                        <Box>
                                                            <Typography fontWeight={600}>Copy English to VO</Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                Copies the English SRT directly to "English-VO" without
                                                                invoking the AI server. Use this when no AI adaptation is
                                                                needed.
                                                            </Typography>
                                                        </Box>
                                                    }
                                                />
                                            </RadioGroup>

                                            <List sx={{ py: 0 }}>
                                                <ListItem sx={{ px: 0, py: 1 }}>
                                                    <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                                                        <EditNote />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary="You can use English-VO in the dropdown in Subtitle Edit View to select the language and make changes or review."
                                                        sx={{ '& .MuiListItemText-primary': { fontWeight: 500 } }}
                                                    />
                                                </ListItem>

                                                <ListItem sx={{ px: 0, py: 1 }}>
                                                    <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                                                        <Translate />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary="You can use English-VO in the T2TT as the source language for other languages."
                                                        sx={{ '& .MuiListItemText-primary': { fontWeight: 500 } }}
                                                    />
                                                </ListItem>
                                            </List>
                                        </Paper>
                                    </Box>
                                </TabPanel>
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={() => onClose()} disabled={loading}>
                    Close
                </Button>
                {englishAvailable && (
                    <Button variant="contained" color="primary" onClick={handleGenerateVoiceOver} disabled={loading}>
                        {voMode === 'copy' ? 'Copy English to VO' : 'Generate VO Script'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default VoiceOverDialog;
